import { useMemo, useState } from "react";
import { useTenantOrganization } from "../../../lib/api";
import {
  filterPeople,
  groupPeopleByWorkshop,
  indexOrg,
  personRemovalNote,
  personRows,
} from "../../../lib/organization";
import { usePersonRemoval } from "../../SuperAdmin/useOrgEditors";
import { Icon } from "../../../components/msd/Icon";
import {
  Avatar,
  Btn,
  CARD,
  Empty,
  Page,
  PageHead,
  SearchBox,
  Seg2,
  SelectBox,
  Tag,
  cx,
} from "../../../components/msd/ui";
import PersonSheet from "./PersonSheet";
import { ConfirmDelete, OrgError, OrgLoading, plural } from "./kit";

const STATUS = {
  active: ["green", "Active"],
  invited: ["orange", "Invited"],
  disabled: [null, "Disabled"],
  no_login: [null, "No login"],
};

const STATUS_FILTER = [
  ["all", "All"],
  ["active", "Active"],
  ["invited", "Invited"],
  ["no_login", "No login"],
];

// .pp-row
const ROW =
  "flex w-full flex-wrap items-center gap-[14px] rounded-xl border-0 bg-transparent p-[10px] text-left text-inherit [&+&]:shadow-[0_-1px_0_var(--line)]";

/**
 * People: everyone who works here — logins, workshop stakeholders and service
 * advisors — in one card per workshop. The same list, filters and writes as
 * Super Admin's People page, for the signed-in brand.
 */
export default function PeoplePage({ readOnly }) {
  const { data, isLoading, isError, error, refetch } = useTenantOrganization(null);
  const [filters, setFilters] = useState({ query: "", role: "all", dealer: "all", workshop: "all", status: "all" });
  // The person in the sheet: false when closed, null when adding.
  const [sheet, setSheet] = useState(false);

  const ix = useMemo(() => indexOrg(data), [data]);
  const rows = useMemo(() => personRows(ix), [ix]);
  const shown = useMemo(() => filterPeople(rows, filters), [rows, filters]);
  const groups = useMemo(() => groupPeopleByWorkshop(shown, ix), [shown, ix]);
  const { deleting, setDeleting, askRemove, confirmRemove, pending } = usePersonRemoval(null, ix);

  if (isLoading) return <OrgLoading />;
  if (isError) return <OrgError error={error} onRetry={refetch} />;

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const openAdd = () => setSheet(null);
  const rowById = new Map(rows.map((r) => [r.id, r]));

  // Built from the rows on screen, not from the whole org: a filter offering
  // a dealer that nobody here works for only ever empties the list.
  const optionsFrom = (pick) => [...new Set(rows.map(pick).filter(Boolean))].sort();

  const remove = async () => {
    if (await confirmRemove()) setSheet(false);
  };

  const row = (r) => {
    const [tone, word] = STATUS[r.status] || [null, r.status];
    const inner = (
      <>
        <Avatar name={r.name} size={40} />
        <span className="min-w-0 flex-[1_1_200px]">
          <b className="block truncate text-[15px] font-semibold">{r.name}</b>
          <span className="block truncate text-[13px] text-msd-muted">{r.email || "No login"}</span>
        </span>
        <span className="min-w-0 flex-[0_1_190px] text-[14px] text-msd-ink2 max-[640px]:basis-full max-[640px]:pl-[54px]">
          {r.roleLabel}
          {r.access !== "—" ? <span className="block truncate text-[12.5px] text-msd-muted">{r.access}</span> : null}
        </span>
        <span className="flex flex-[0_1_220px] items-center gap-2 text-[14px] tabular-nums max-[640px]:basis-full max-[640px]:pl-[54px]">
          {r.number || <span className="text-[13px] text-msd-muted">No number</span>}
        </span>
        <Tag tone={tone}>{word}</Tag>
        {!readOnly && (
          <span className="flex text-msd-faint">
            <Icon name="right" size={16} stroke={2} />
          </span>
        )}
      </>
    );
    // A read-only login sees the row but has nothing to open.
    return readOnly ? (
      <div key={r.id} className={ROW} data-testid="person-row">
        {inner}
      </div>
    ) : (
      <button
        key={r.id}
        type="button"
        className={cx(ROW, "cursor-pointer hover:bg-msd-soft")}
        data-testid="person-row"
        aria-label={`Edit ${r.name}`}
        onClick={() => setSheet((ix.people || []).find((p) => p.id === r.id))}
      >
        {inner}
      </button>
    );
  };

  return (
    <Page>
      <PageHead title="People" sub={plural(rows.length, "person", "people")}>
        {!readOnly && (
          <Btn variant="pri" icon="plus2" onClick={openAdd}>
            Add person
          </Btn>
        )}
      </PageHead>

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-[10px]">
          <SearchBox
            value={filters.query}
            onChange={(v) => set({ query: v })}
            placeholder="Search people"
            testid="people-search"
          />
          <SelectBox value={filters.role} onChange={(v) => set({ role: v })} label="Role">
            <option value="all">Any role</option>
            {optionsFrom((r) => r.roleLabel).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </SelectBox>
          <SelectBox value={filters.dealer} onChange={(v) => set({ dealer: v })} label="Dealer">
            <option value="all">Any dealer</option>
            {optionsFrom((r) => r.dealerName).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </SelectBox>
          <SelectBox value={filters.workshop} onChange={(v) => set({ workshop: v })} label="Workshop">
            <option value="all">Any workshop</option>
            {optionsFrom((r) => r.workshopLocation).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </SelectBox>
          <Seg2 options={STATUS_FILTER} value={filters.status} onChange={(v) => set({ status: v })} label="Status" />
        </div>
      )}

      {rows.length === 0 ? (
        <Empty title="Nobody has a login yet">
          Add the first one.
          {!readOnly && (
            <div className="mt-3">
              <Btn variant="pri" icon="plus2" onClick={openAdd}>
                Add person
              </Btn>
            </div>
          )}
        </Empty>
      ) : shown.length === 0 ? (
        <Empty title="Nobody matches those filters">Clear a filter, or add the person who should be here.</Empty>
      ) : (
        groups.map((g) => (
          <section key={g.key || "_none"} className={cx(CARD, "px-[10px] py-2")}>
            <div className="px-[10px] pb-[6px] pt-[10px] text-[13px] font-semibold text-msd-ink2">
              {g.name || "No single workshop"}
              {g.dealer ? <span className="font-normal text-msd-muted"> · {g.dealer}</span> : null}{" "}
              <span className="font-normal text-msd-muted">{g.people.length}</span>
            </div>
            {g.people.map(row)}
          </section>
        ))
      )}

      <PersonSheet
        open={sheet !== false && !deleting}
        onOpenChange={(v) => !v && setSheet(false)}
        ix={ix}
        person={sheet || null}
        // Everyone can be removed, login or not; the prefixed id tells the
        // API which table the row is in.
        onRemove={readOnly ? null : (p) => askRemove(p.id, rowById.get(p.id)?.hasLogin)}
      />

      <ConfirmDelete
        open={!!deleting}
        onClose={() => setDeleting(null)}
        name={deleting?.name || ""}
        body={personRemovalNote(deleting?.hasLogin)}
        onConfirm={remove}
        pending={pending}
      />
    </Page>
  );
}
